import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Preferences} from '@capacitor/preferences';
import {firstValueFrom} from 'rxjs';
import {AuthSession} from '../../auth/auth.models';
import {buildApiUrl} from '../api/api-url';
import {isNativeCapacitorPlatform} from '../platform';
import {AppPackageInfoPlugin} from './app-package-info.plugin';

const INSTALLATION_ID_KEY = 'invemory.android.installationId';

@Injectable({providedIn: 'root'})
export class AndroidVersionObservationService {
  private installationIdPromise: Promise<string> | null = null;

  constructor(private readonly http: HttpClient) {}

  async observe(session: AuthSession | null): Promise<void> {
    if (!session || !isNativeCapacitorPlatform()) return;

    try {
      const [info, installationId] = await Promise.all([AppPackageInfoPlugin.getInfo(), this.installationId()]);
      const version = `${info.versionName}-build.${info.versionCode}`.slice(0, 48);
      await firstValueFrom(this.http.post(buildApiUrl('/v1/auth/android-version-observations'), {
        firstInstalledAt: info.firstInstalledAt,
        installationId,
        lastUpdatedAt: info.lastUpdatedAt,
        version,
      }, {headers: {Authorization: `Bearer ${session.accessToken}`}}));
    } catch {
      // Version observation is best effort and must never block sign-in or a restored session.
    }
  }

  private installationId(): Promise<string> {
    this.installationIdPromise ??= this.readOrCreateInstallationId();
    return this.installationIdPromise;
  }

  private async readOrCreateInstallationId(): Promise<string> {
    const stored = await Preferences.get({key: INSTALLATION_ID_KEY});
    if (stored.value) return stored.value;
    const id = crypto.randomUUID();
    await Preferences.set({key: INSTALLATION_ID_KEY, value: id});
    return id;
  }
}
